import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { getRomanNumeral, getDegreeNumber } from './chordUtils';

export const generatePDF = async (songData) => {
  try {
    const pdfDoc = await PDFDocument.create();
    let page = pdfDoc.addPage();
    // Use Courier for monospace formatting
    const font = await pdfDoc.embedFont(StandardFonts.Courier);
    const boldFont = await pdfDoc.embedFont(StandardFonts.CourierBold);

    const { width, height } = page.getSize();
    let currentY = height - 50;
    const margin = 50;
    const lineHeight = 25;
    const chordHeight = 12;
    const charWidth = 7;

    // Helper function to clean text for PDF while preserving spaces
    const cleanText = (text, preserveSpaces = false) => {
      if (!text) return '';
      let cleaned = text
        .replace(/[^\x00-\x7F]/g, '') // Remove non-ASCII characters
        .replace(/[""'']/g, '"'); // Replace smart quotes
      
      return preserveSpaces ? cleaned : cleaned.trim();
    };

    // Title and key at the top
    page.drawText(cleanText(`${songData.title} - Key: ${songData.key}`), {
      x: margin,
      y: currentY,
      size: 24,
      font: boldFont,
    });
    currentY -= 40;

    // Loop through sections
    for (const section of songData.sections) {
      if (currentY < 100) {
        page = pdfDoc.addPage();
        currentY = height - 50;
      }

      // Section name
      page.drawText(cleanText(section.type), {
        x: margin,
        y: currentY,
        size: 14,
        font: boldFont,
        color: rgb(0.4, 0.4, 0.4),
      });
      currentY -= 20;

      // Process content
      for (const line of section.content) {
        if (currentY < 100) {
          page = pdfDoc.addPage();
          currentY = height - 50;
        }

        // Draw chords with preserved spacing
        if (line.chords !== undefined) {
          const chords = cleanText(line.chords, true);
          page.drawText(chords, {
            x: margin,
            y: currentY + chordHeight,
            size: 10,
            font: font,
            color: rgb(0, 0, 0.8),
          });
        }

        // Draw lyrics
        if (line.lyrics) {
          const lyrics = cleanText(line.lyrics, true);
          page.drawText(lyrics, {
            x: margin,
            y: currentY,
            size: 11,
            font: font,
          });
        }

        currentY -= lineHeight;
      }

      currentY -= 15;
    }

    return await pdfDoc.save();
  } catch (error) {
    console.error('PDF Generation Error:', error);
    throw new Error('Failed to generate PDF: ' + error.message);
  }
};

export const generateRomanPDF = async (songData) => {
  try {
    const pdfDoc = await PDFDocument.create();
    let page = pdfDoc.addPage();
    const font = await pdfDoc.embedFont(StandardFonts.Courier);
    const boldFont = await pdfDoc.embedFont(StandardFonts.CourierBold);
    const { width, height } = page.getSize();
    let currentY = height - 50;
    const margin = 50;
    const lineHeight = 25;
    // Collect all unique roman numerals in order of appearance
    const numeralsSet = [];
    for (const section of songData.sections) {
      for (const line of section.content) {
        if (line.chords !== undefined) {
          line.chords.split(/\s+/).forEach(part => {
            const numeral = part.trim() ? getRomanNumeral(part, songData.key) : null;
            if (numeral && !numeralsSet.includes(numeral)) numeralsSet.push(numeral);
          });
        }
      }
    }
    // Title and key at the top
    page.drawText(`${songData.title} - Key: ${songData.key} (Numbers)`, {
      x: margin,
      y: currentY,
      size: 24,
      font: boldFont,
    });
    currentY -= 30;
    // Show numerals used centered under the title
    if (numeralsSet.length > 0) {
      const numeralsText = `(${numeralsSet.join(', ')})`;
      const textWidth = font.widthOfTextAtSize(numeralsText, 14);
      page.drawText(numeralsText, {
        x: (width - textWidth) / 2,
        y: currentY,
        size: 14,
        font: font,
        color: rgb(0.8, 0, 0),
      });
      currentY -= 20;
    }
    for (const section of songData.sections) {
      if (currentY < 100) {
        page = pdfDoc.addPage();
        currentY = height - 50;
      }
      // Section name
      page.drawText(section.type, {
        x: margin,
        y: currentY,
        size: 14,
        font: boldFont,
        color: rgb(0.64, 0.09, 0.09), // dark red
      });
      currentY -= 20;
      for (const line of section.content) {
        if (currentY < 100) {
          page = pdfDoc.addPage();
          currentY = height - 50;
        }
        // Roman numerals line
        if (line.chords !== undefined) {
          const numerals = line.chords
            .split(/(\s+)/)
            .map(part => part.trim() ? getRomanNumeral(part, songData.key) || '-' : part)
            .join('');
          page.drawText(numerals, {
            x: margin,
            y: currentY,
            size: 12,
            font: font,
            color: rgb(0.8, 0, 0), // red
          });
        }
        // Lyrics line
        if (line.lyrics) {
          page.drawText(line.lyrics, {
            x: margin,
            y: currentY - 13,
            size: 11,
            font: font,
            color: rgb(0, 0, 0), // dark grey
          });
        }
        currentY -= lineHeight;
      }
      currentY -= 15;
    }
    return await pdfDoc.save();
  } catch (error) {
    console.error('Roman PDF Generation Error:', error);
    throw new Error('Failed to generate Roman numeral PDF: ' + error.message);
  }
}; 